(ns basic-components.field-editor
  (:require
   [common.localization :as loc]
   [common.url :as url]
   [frontend.utils :as fu]
   [basic-components.inputs.footprint :refer [editor viewer display-string]]
   [frontend.view.components.clipboard :as clipboard]
   [frontend.dom-utils :as dom-utils]
   [reagent.core :as r]
   [frontend.state.current-session :as session]
   [frontend.routing :as routing]
   [clojure.string :as string]
   [frontend.logging :as logging]
   [frontend.bootstrap.components :as rbs]
   [basic-components.popover :as popover]
   [common.schema.registry :as registry]
   [reagent.dom :as rd]
   [ui.util :as uu]))

(def tools-shown-unmodified
  {:tool-item/debug    {:icon     (fu/cs :fal :fa-debug)
                        :on-click (fn [e attribute local-atom editor-atom args]
                                    (swap! editor-atom assoc :debug? (not (:debug? @editor-atom))))}
   :tool-item/clear    {:icon     (fu/cs :fal :fa-times :tool-item__clear)
                        :on-click (fn [e attribute local-atom editor-atom args on-input-blur!
                                       on-input-change!]
                                    (when on-input-change!
                                      (on-input-change! nil attribute))
                                    (when on-input-blur!
                                      (on-input-blur! attribute nil nil identity)))}
   :tool-item/copy     {:icon     (fu/cs :fal :fa-copy)
                        :on-click (fn [e attribute local-atom editor-atom args]
                                    (clipboard/copy-to
                                     (str
                                      (display-string args)
                                      (when (:secondary-view-args args)
                                        (str " " (display-string (:secondary-view-args args))))))
                                    (.preventDefault e))}
   :tool-item/open-url {:icon     (fu/cs :fal :fa-external-link)
                        :on-click (fn [e attribute local-atom editor-atom args]
                                    (let [value (:value args)
                                          url-value (when (= :editor.type/url (:editor/type args))
                                                      (url/relative-or-protocol-url
                                                       (str value)
                                                       :https))]
                                      (if (= :editor.type/url (:editor/type args))
                                        (when-let [sanitized (fu/sanitize-url url-value)]
                                          (.open js/window sanitized "_blank")) ;; open urls
                                        (routing/open-entity (:value args)))
                                      (.preventDefault e)))}})
(def tools-shown-modified
  {:tool-item/save   {:icon     (fu/cs :fal :fa-save)
                      :on-click (fn [e attribute local-atom editor-atom args]
                                  (when-let [save! (:save! @editor-atom)]
                                    (save!))
                                  (.preventDefault e))}
   :tool-item/cancel {:icon     (fu/cs :fal :fa-undo)
                      :on-click (fn [e attribute local-atom editor-atom args]
                                  (when-let [cancel! (:cancel-edit! @editor-atom)]
                                    (let [{:keys [:validate! :attribute]} args]
                                      (cancel!)
                                      (swap! local-atom dissoc :has-updates? :editor-value)
                                      (when validate!
                                        (let [error-text (validate! attribute
                                                                    (or (:editor-value @local-atom)
                                                                        (:value args)))]
                                          (if error-text
                                            (swap! editor-atom assoc :error error-text)
                                            (swap! editor-atom dissoc :error))))))
                                  (.preventDefault e))}})
(def tools-shown-on-hover
  {:tool-item/edit     {:icon     (fu/cs :fal :fa-edit)
                        :on-click (fn [e attribute local-atom editor-atom args]
                                    (when-let [focus! (:set-focusable-element! @editor-atom)]
                                      (focus!))
                                    (.preventDefault e))}
   :tool-item/settings {:icon     (fu/cs :fal :fa-cog)
                        :on-click (fn [e attribute local-atom editor-atom
                                       {:keys [open-settings!] :as args}]
                                    (when open-settings!
                                      (open-settings!)))}})

(defn resolve-editor-type
  [view-args]
  (let [editor-type (or (:editor/type view-args)
                        (:editor/type (:field view-args)))]
    (if-not editor-type
      (logging/error "Editor type not found : " view-args)
      editor-type)))

(defn render
  [local-atom drop-count editor-atom
   {:keys [:id
           :entity-type
           :entity-unique-name
           :value
           :state-atom
           :attribute :label :hide-label?
           :on-save!
           :on-change!
           :class-name
           :mandatory? :drag-options
           :source-options :validate!
           :disabled? :read-only?
           :info-text
           :error-text
           :on-focus
           :on-blur
           :tools :secondary-view-args
           :field/tooltip-right]
    :as   view-args}]
  (when-not attribute
    (logging/error "NO Attribute : " view-args))
  (let [editor-dom-node (:editor-dom-node @editor-atom)
        has-focus? (:has-focus? @editor-atom)
        secondary-attribute (:attribute secondary-view-args)
        on-input-focus! (fn [e]
                          (when on-focus
                            (on-focus attribute))
                          (swap! editor-atom assoc :has-focus? true)
                          true)
        on-input-change! (fn [e edit-value]
                           (if (= edit-value value)
                             (swap! local-atom dissoc :has-updates?)
                             (swap! local-atom assoc :has-updates? true))
                           (swap! local-atom assoc :editor-value edit-value)
                           (when validate!
                             (let [error-text (validate! attribute edit-value)]
                               (if error-text
                                 (do
                                   ;(logging/error "Field editor error on-input-change!: " error-text " << " edit-value)
                                   (swap! editor-atom assoc :error error-text))
                                 (swap! editor-atom dissoc :error))))
                           (when on-change!
                             (on-change! attribute edit-value))
                           true)
        on-input-blur!
        (fn [blurred-attribute e value-on-blur clear-temp-cb]
          (let [related-target (some-> e
                                       .-relatedTarget)
                value-on-blur (if (string? value-on-blur)
                                (string/trim value-on-blur)
                                value-on-blur)
                field-args (if (= blurred-attribute secondary-attribute)
                             secondary-view-args
                             view-args)
                clear? (or (nil? value-on-blur)
                           (and (string? value-on-blur)
                                (empty? (string/trim value-on-blur))))]
            (swap! editor-atom dissoc :has-focus?)
            (when on-blur
              (on-blur attribute))
            (if (and clear? (:field/not-clearable? field-args)) ;; if value must not be cleared - error msg
              (do
                (swap! editor-atom dissoc :has-focus?)
                (swap! editor-atom assoc :error (loc/tr :value-cannot-be-cleared)))
              (do
                (if-not related-target
                  (swap! editor-atom dissoc :has-focus?)
                  (when-not (dom-utils/is-ancestor? related-target editor-dom-node)
                    (swap! editor-atom dissoc :has-focus?)))
                (when (fn? on-save!)
                  (let [error-text (when validate! (validate! blurred-attribute value-on-blur))]
                    ;; timeout for clearing internal state ? or do we handle this outside ?
                    ;; on success
                    ;; must also think about error messages !!!
                    (if error-text
                      (do #_(logging/error "Field editor error on-input-blur!: " error-text
                                           " << " value-on-blur))
                      (on-save!
                       blurred-attribute
                       value-on-blur
                       (fn [_]
                         (swap! editor-atom dissoc :error)
                         (when clear-temp-cb
                           (swap! local-atom dissoc :has-updates? :editor-value)
                           (clear-temp-cb))) ;; clear temp state
                       (fn [err]
                         (let [error-msg (:message err)]
                           (logging/error "ERROR saving value : " error-msg " " value-on-blur)
                           (when error-msg
                             (swap! editor-atom assoc :error (str error-msg))))
                         ;; TODO: error handling - do not reset temp state -> or user would lose temp data on error
                         )))))))))

        label (or (and (string? label) label)
                  (and (fn? label) (label view-args))
                  (when-let [attr (:attribute view-args)]
                    (let [tenant-schema @session/tenant-schema
                          fs (registry/get-field-schema tenant-schema attr)]
                      (loc/field-name attr fs))))
        info-text (or (and (string? info-text) info-text)
                      (and (fn? info-text) (info-text view-args)))
        error (:error @editor-atom)
        error-text-secondary (:error-text-secondary secondary-view-args)
        error-text (or error error-text-secondary)
        {:keys [draggable? hide-drag-area?
                on-drag-start! on-drag-end!
                on-drag-leave on-drag-enter on-drag-over]}
        drag-options
        {:keys [:source-icon-fn]} source-options
        hide-drag-area? (if (fn? hide-drag-area?)
                          (hide-drag-area? view-args)
                          (boolean hide-drag-area?))
        draggable? (if (fn? draggable?)
                     (draggable? view-args)
                     (boolean draggable?))
        drag-attribute (:dragging? view-args)
        drop-location (:drop view-args)
        primary-editor-type (resolve-editor-type view-args)
        tooltip-right (:field/tooltip-right view-args)
        field-mandatory? (if (fn? mandatory?)
                           (mandatory? view-args)
                           (boolean mandatory?))]
    ^{:key (str "basic-components-field-editor-" attribute)}
    [:div
     (merge
      {:ref        (fn [e]
                     (when e
                       ;; we use this to know where focus is changing - we can prevent blur if focus is changing to
                       ;; events withing this editor if needed
                       (swap! editor-atom assoc :editor-dom-node e)))
       :class-name (fu/cs (str "input-editor-class__"
                               (when (keyword? entity-type)
                                 (name entity-type))
                               "__" (when (keyword? attribute)
                                      (namespace attribute))
                               "_" (when (keyword? attribute)
                                     (name attribute)))
                          (when (keyword? attribute)
                            (str (namespace attribute) "_" (name attribute)))
                          (str
                           "entity-name-class__"
                           (string/replace (str entity-unique-name)
                                           #"\s"
                                           ""))

                          "input-editor"
                          (when has-focus? "input-editor--focused")
                          (when class-name class-name)
                          (when (= drag-attribute attribute)
                            "dragging")
                          (when (and (> drop-count 0)
                                     (not= drag-attribute attribute))
                            (str "dropzone"
                                 (if (= drop-location :above)
                                   " above"
                                   " below")))
                          (when (some #{(:editor/type view-args)}
                                      [:editor.type/select :editor.type/multi-select])
                            "select-area")
                          (when (some #{(:editor/type view-args)}
                                      [:editor.type/datetime :editor.type/date])
                            "datepicker__wrapper"))
       :draggable  (boolean draggable?)}
      (when draggable?
        {:on-drag-start (fn [e]
                          (aset e "dataTransfer" "effectAllowed" "move")
                          ;;drag data must be set for dragend to fire in firefox:
                          (.. e -dataTransfer (setData "text" "zapflow"))

                          (on-drag-start! attribute)
                          (.stopPropagation e))
         :on-drag-end   (fn [me]
                          (.preventDefault me)
                          (.stopPropagation me)

                          (on-drag-end! attribute))
         :on-drop       (fn [e])
         :on-drag-leave (fn [oe]
                          (let [target (->> oe
                                            .-target)
                                already-counted (some #{(str target)} (:drag-counted @local-atom))
                                new-count (dec (:drop-count @local-atom))]
                            (swap! local-atom assoc
                                   :drag-counted
                                   (disj (:drag-counted @local-atom) (str target)))
                            (if already-counted
                              (do
                                (swap! local-atom assoc :drop-count new-count))
                              (logging/log "Skip DECREASE :" attribute "->" new-count "-" target))

                            (when (= (:drop-count @local-atom) 0)
                              (on-drag-leave attribute))
                            (.stopPropagation oe)
                            (.preventDefault oe)))
         :on-drag-over  (fn [e]
                          (.preventDefault e))
         :on-drag-enter (fn [ee]
                          (let [target (->> ee
                                            .-target)
                                already-counted (some #{(str target)} (:drag-counted @local-atom))
                                new-count (inc (:drop-count @local-atom))]
                            (swap! local-atom assoc
                                   :drag-counted
                                   (conj (:drag-counted @local-atom) (str target)))
                            (if-not already-counted
                              (do
                                (swap! local-atom assoc :drop-count new-count))
                              (do
                                (logging/log "SKIP Increase - " already-counted)))
                            (when (= (:drop-count @local-atom) 1)
                              (on-drag-enter attribute))
                            (.stopPropagation ee)
                            (.preventDefault ee)))}))
     (when-not hide-drag-area?
       [:div
        {:class-name (fu/cs "input-editor__handle"
                            (when draggable? "draggable")
                            (when read-only? "readonly")
                            (when disabled? "disabled"))}
        (when draggable?
          [:i.fal.fa-grip-vertical])])
     [:div.input-editor__content
      {:draggable     false ;; Prevent drag start events from content area
       :on-drag-start (fn [e]
                        (.preventDefault e)
                        (.stopPropagation e))}

      [:div.input-editor__header
       (when source-icon-fn
         [:div.input-editor__source
          [source-icon-fn view-args]])
       (when (and (not hide-label?)
                  (not= :editor.type/checkbox primary-editor-type))
         [:div.input-editor__label
          (if tooltip-right
            [popover/view nil tooltip-right
             [:label
              {:class-name (fu/cs "input-editor__label-text")}
              label
              [:i
               {:class-name (fu/cs :input-editor__info-icon
                                   :fas
                                   :fa-info-circle
                                   :ml-2)}]] false {:placement "right"}]
            (fu/overlay-if
             (:field/label-icon-tooltip view-args)
             [rbs/OverlayTrigger
              {:placement     "top"
               :popper-config {:modifiers [{:name    "preventOverflow"
                                            :options {:main-axis false}}]}
               :overlay       (r/as-element
                               [rbs/Tooltip
                                {:class "in modal-tooltip"
                                 :style {:white-space "pre-wrap"}
                                 :id    (str "editor-tooltip-" (name attribute))}
                                (:field/label-icon-tooltip view-args)])}]

             [:label
              {:class-name (uu/cm "input-editor__label-text"
                                  (when field-mandatory?
                                    "after:ml-0.5 after:text-danger-regular after:content-['*']"))}
              label
              (when (:field/label-icon view-args)
                [:i
                 {:class-name (fu/cs :input-editor__icon :fal (:field/label-icon view-args))}])
              #_(when field-mandatory?
                  [:i.fas.fa-asterisk.input-editor__label-mandatory])]))

          [:span.input-field__empty-space]])
       [:ul
        {:class-name (fu/cs :input-editor__tools (when hide-label? :no-label))}
        (doall
         (for [tool tools]
           (let [shown-on-focus? (get tools-shown-modified tool)
                 shown-on-hover? (get tools-shown-on-hover tool)
                 shown-when-no-updates? (get tools-shown-unmodified tool)
                 tool-config (or shown-on-focus? shown-on-hover? shown-when-no-updates?)]
             ;; A) cancel tool not available for not cancellable elements
             ;; B) open link tool available only
             ;; i) url, link and email types support open link
             ;; ii) single entity types that have a dedicated view can be opened with link
             (when
              (and
               (not (and (some #{tool} [:tool-item/cancel :tool-item/save])
                         (or
                          (not (:has-updates? @local-atom)) ;; don't show cancel/save when no updates
                          (some #{primary-editor-type}
                                [:editor.type/toggle :editor.type/checkbox :editor.type/radio
                                 :editor.type/financials :editor.type/tesi-financials])
                          disabled?
                          read-only?)))
               (or (not= tool :tool-item/open-url)
                   (some #{primary-editor-type}
                         [:editor.type/url :editor.type/link :editor.type/email])
                   (and (= primary-editor-type :editor.type/select)
                        (some (set (:field/entity-types view-args))
                              [:entity.type/person :entity.type/company :entity.type/oppo])
                        (:value view-args)))
               (or (not= tool :tool-item/debug)
                   (and (some #{primary-editor-type}
                              [:editor.type/select :editor.type/multi-select])
                        (session/sys-admin?)))            ;; only sys-admin can debug selects
               (not (and (some #{tool} [:tool-item/edit]) ;; no edit on read-only or disabled fields
                         (or disabled?
                             (some #{primary-editor-type}
                                   [:editor.type/financials :editor.type/tesi-financials
                                    :editor.type/link
                                    :editor.type/select :editor.type/multi-select])
                             read-only?)))
               (not (and (some #{tool} [:tool-item/clear]) ;; no clear for empty or non-clearable
                         (or                               ;(not (some #{primary-editor-type} [:editor.type/select :editor.type/multi-select]))
                          read-only?
                          (nil? value)
                          (:field/not-clearable? view-args)
                          (:not-clearable? view-args))))
               (not (and (some #{tool} [:tool-item/copy]) ;; no copy for empty values
                         (or (nil? value)
                             (and (string? value) (empty? value))
                             (and (coll? value) (empty? value))))))
               (if (or (and (not (:has-updates? @local-atom)) shown-when-no-updates?)
                       (and has-focus? shown-on-focus?)
                       shown-on-hover?)
                 [:li
                  {:key           (str (:namespace (:attribute view-args))
                                       (:name (:attribute view-args))
                                       tool)
                   :class-name    (fu/cs "input-editor__tools-item"
                                         (if shown-on-hover?
                                           "hover"
                                           "input-editor__tools-item--focused"))
                   :tab-index     -1
                   :on-mouse-down (fn [e]
                                    ((:on-click tool-config)
                                     e
                                     attribute
                                     local-atom
                                     editor-atom
                                     view-args
                                     on-input-blur!
                                     on-input-change!))}
                  [:i {:class-name (:icon tool-config)}]])))))]]
      [:div.input-editor__input
       (when read-only?
         [viewer view-args])
       (when (and secondary-attribute secondary-view-args)
         [:div
          {:class-name (fu/cs "input-editor__input"
                              (when (= (:field/data-type secondary-view-args)
                                       :data.type/currency-symbol)
                                "currency-symbol__container"))}
          (if read-only?
            [viewer secondary-view-args]
            [editor
             (merge secondary-view-args
                    {:editor/type      (resolve-editor-type secondary-view-args)
                     :state-atom       editor-atom
                     :unique-id        entity-unique-name
                     :max-opts-visible (when (= (:field/data-type secondary-view-args)
                                                :data.type/currency-symbol)
                                         5)
                     :has-focus?       has-focus?
                     :class-name       (fu/cs (when error-text-secondary "error")
                                              (when read-only? "readonly")
                                              (when disabled? "disabled")
                                              "secondary-input")
                     :on-focus!        on-input-focus!
                     :on-blur!         (partial on-input-blur! secondary-attribute)})])])
       (when-not read-only?
         [:div.input-editor__primary-content
          [editor
           (merge
            view-args
            {:editor/type            primary-editor-type
             :state-atom             editor-atom
             :unique-id              entity-unique-name
             :debug?                 (:debug? @editor-atom)
             :has-focus?             has-focus?
             :class-name             (fu/cs (when error-text "error")
                                            (when read-only? "readonly")
                                            (when disabled? "disabled")
                                            (when (and secondary-attribute secondary-view-args)
                                              "primary-input"))
             :set-focusable-element! (fn [e]
                                       (swap! editor-atom assoc :set-focusable-element! e))
             :set-cancel-edit!       (fn [e]
                                       (swap! editor-atom assoc :cancel-edit! e))
             :set-save!              (fn [e]
                                       (swap! editor-atom assoc :save! e))
             :on-change!             on-input-change!
             :on-error!              (fn [error-text]
                                       (swap! editor-atom assoc :error error-text))
             :clear-error!           (fn []
                                       (swap! editor-atom dissoc :error))
             :on-focus!              on-input-focus!
             :on-blur!               (partial on-input-blur! attribute)})]])]
      [:div.input-editor__primary-content
       (when-not read-only?
         (if error-text
           [:span.input-editor__primary-content.input-editor__primary-content--error
            error-text]
           (when info-text
             [:span.input-editor__info-text
              info-text])))]]]))

(defn view
  [{:keys [editor/type           ;; this is used by the multimethod to dispatch to correct input implementation
           value                 ;; controlled element -> you must control the rendered value outside of the editor -> use state-atom to store value or directly to back end entity
           state-atom            ;; this is used to store the state
           attribute             ;; this is and identifier for the edited input -> it can be a custom attribute for the view or it can be an entity field :email / :oppo/name ...
           on-save!              ;; this is used for notifying containing logic of end of edit -> change state outside and make sure to update the value param -> controlled state
           label                 ;; visible label for input element - f.e. "Name" (loc/tr :oppo/name)
           refresh-info-atom     ;; optional - This is used to indicate when we want to update the editor-atom value programmatically. Used in vainu data enrichment
           options               ;; optional - select editor options list [{:key :option-1 :label "Option1"} {:key 123031023 :label "My option"} {:key "my-key" :label "My Label"}]
           placeholder           ;; optional - placeholder text for input elem when value is empty
           hide-label?           ;; optional - hide label ? default false
           class-name            ;; optional - additional css class names
           field/tooltip-right   ;; optional - tooltip text
           mandatory?            ;; optional - mandatory field default false
           drag-options          ;; optional - drag and move field handle see ....
           validate!             ;; optional - function to validate the entered value -> text return value is interpreted as error and is shown in the error area
           disabled?             ;; optional - default false
           read-only?            ;; optional - default false
           info-text             ;; optional - info text
           error-text            ;; optional - error text - controlled vs uncontrolled see :validate! param
           tools                 ;; optional - enabled tool icons see ...
           secondary-view-args   ;; optional - secondary input - f.e. currency has currency symbol select box as secondary input and number input as primary input type
           on-blur               ;; optional - input blur callback
           on-focus              ;; optional - input focus callback
           on-change             ;; optional - input value change callback
           ]}];; only mandatory params required by all editors listed here - additional optional params should be exposed here or documented as well
  (let [editor-atom (r/atom nil) ;(r/cursor state-atom [attribute])
        local-atom (r/atom {:drop-count   0
                            :drag-counted #{}})
        reset-state-if-needed
        (fn []
          (when refresh-info-atom
            (when-let [attributes-to-refresh (:attributes-to-refresh @refresh-info-atom)]
              (when (get attributes-to-refresh attribute)
                (swap! editor-atom assoc :temp-value (get @state-atom attribute))
                (swap! refresh-info-atom assoc-in [:attributes-to-refresh attribute] false)))))]
    (r/create-class
     {:display-name   "input-editor"
      :reagent-render (fn [args]
                        (reset-state-if-needed)
                        (when (and (not (:dragging? args))
                                   (not= (:drop-count @local-atom) 0))
                          (reset! local-atom {:drop-count   0
                                              :drag-counted #{}})) ;; reset local drop counter if we're not dragging
                        (render local-atom
                                (:drop-count @local-atom)
                                editor-atom
                                args))})))
